import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ImageSkeleton from './ImageSkeleton';

const Carousel = ({ images, alt }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [imageErrors, setImageErrors] = useState({});
    const [loadedImages, setLoadedImages] = useState({});

    if (!images || images.length === 0) return null;

    const handleImageError = (index) => {
        setImageErrors(prev => ({ ...prev, [index]: true }));
    };

    const handleImageLoad = (index) => {
        setLoadedImages(prev => ({ ...prev, [index]: true }));
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    // Touch gesture handlers for mobile swipe
    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientX);
        setTouchEnd(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const swipeThreshold = 75; // Minimum swipe distance

        if (distance > swipeThreshold) {
            // Swiped left - go to next
            goToNext();
        } else if (distance < -swipeThreshold) {
            // Swiped right - go to previous
            goToPrevious();
        }

        // Reset touch positions
        setTouchStart(0);
        setTouchEnd(0);
    };

    // Calculate previous and next indices for the 3D effect
    const getPrevIndex = () => currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    const getNextIndex = () => currentIndex === images.length - 1 ? 0 : currentIndex + 1;

    return (
        <div className="carousel-container">
            <div
                className="carousel-3d-wrapper"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Previous Image (Left) */}
                {images.length > 1 && (
                    <div
                        className="carousel-slide-3d carousel-slide-left"
                        onClick={goToPrevious}
                    >
                        {imageErrors[getPrevIndex()] ? (
                            <div className="carousel-image-fallback">
                                <span>Image unavailable</span>
                            </div>
                        ) : (
                            <>
                                {!loadedImages[getPrevIndex()] && (
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <ImageSkeleton width="100%" height="100%" />
                                    </div>
                                )}
                                <img
                                    src={images[getPrevIndex()]}
                                    alt={`${alt} - Previous`}
                                    className="carousel-image-3d"
                                    onError={() => handleImageError(getPrevIndex())}
                                    onLoad={() => handleImageLoad(getPrevIndex())}
                                    loading="lazy"
                                />
                            </>
                        )}
                    </div>
                )}

                {/* Current Image (Center) */}
                <div className="carousel-slide-3d carousel-slide-center">
                    {imageErrors[currentIndex] ? (
                        <div className="carousel-image-fallback">
                            <span>Image unavailable</span>
                        </div>
                    ) : (
                        <>
                            {!loadedImages[currentIndex] && (
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                    <ImageSkeleton width="100%" height="100%" />
                                </div>
                            )}
                            <img
                                src={images[currentIndex]}
                                alt={`${alt} - Screenshot ${currentIndex + 1}`}
                                className="carousel-image-3d"
                                onError={() => handleImageError(currentIndex)}
                                onLoad={() => handleImageLoad(currentIndex)}
                                loading="lazy"
                            />
                        </>
                    )}
                </div>

                {/* Next Image (Right) */}
                {images.length > 1 && (
                    <div
                        className="carousel-slide-3d carousel-slide-right"
                        onClick={goToNext}
                    >
                        {imageErrors[getNextIndex()] ? (
                            <div className="carousel-image-fallback">
                                <span>Image unavailable</span>
                            </div>
                        ) : (
                            <>
                                {!loadedImages[getNextIndex()] && (
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <ImageSkeleton width="100%" height="100%" />
                                    </div>
                                )}
                                <img
                                    src={images[getNextIndex()]}
                                    alt={`${alt} - Next`}
                                    className="carousel-image-3d"
                                    onError={() => handleImageError(getNextIndex())}
                                    onLoad={() => handleImageLoad(getNextIndex())}
                                    loading="lazy"
                                />
                            </>
                        )}
                    </div>
                )}

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <button
                            className="carousel-button-3d carousel-button-left"
                            onClick={goToPrevious}
                            aria-label="Previous slide"
                        >
                            <FaChevronLeft />
                        </button>

                        <button
                            className="carousel-button-3d carousel-button-right"
                            onClick={goToNext}
                            aria-label="Next slide"
                        >
                            <FaChevronRight />
                        </button>
                    </>
                )}
            </div>

            {/* Dots Indicator */}
            {images.length > 1 && (
                <div className="carousel-dots">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}

            {/* Counter */}
            {images.length > 1 && (
                <div className="carousel-counter">
                    {currentIndex + 1} / {images.length}
                </div>
            )}
        </div>
    );
};

export default Carousel;
